/*
 * ServicePartnerList.tsx
 * This Service Partner list Component is used for representing all the saved partners in list Form.
 */
// Import necessary modules and components
import useFetch from "../../../hooks/useFetch";
import { useContext, useEffect, useState } from "react";
import { PageHeader } from "../../../components/page-header/page-header";
import GenericPopover from "../../../components/pop-over";
import useToast from "../../../hooks/useToast";
import Utils from "../../../utils";
import { TFilterModel } from "../../../types/config";
import {
  eColumnConfigKeys,
  ePrivileges,
  eResultCode,
} from "../../../utils/enum";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import { debounceTime, defaultFilterParams } from "../../../utils/constants";
import {
  Button,
  Drawer,
  Input,
  Space,
  Table,
  TablePaginationConfig,
  Tooltip,
} from "antd";
import { TConfigGroup } from "../../../types/config";
import CustomDrawer from "../../../components/drawer";
import { AddEditProduct, GetProductList } from "../../../utils/api.constant";
import { useNavigate } from "react-router-dom";
import utils from "../../../utils";
import ActionCard from "../../../components/actionCard/actionCard";
import { UserContext } from "../../../context";
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  FilterOutlined,
  IdcardOutlined,
  MoreOutlined,
  OrderedListOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import ColumnManager from "../../../components/configurable-columns";
import ExportToCSV from "../../../components/exporttocsv/ExportToCsv";
import DateUtils from "../../../utils/date";
import { TProduct } from "../../../types";
import ConfigProductForm from "./form";
import { ColumnConfigManager } from "../../../utils/column-management";
import CustomModal from "../../../components/modal";
import { render } from "@testing-library/react";

/**
 * ServicePartnerList Component
 * This component renders the list of service partners, with
 * the ability to search, filter, and add new service partners.
 * It also includes a bulk upload feature for importing
 * service partners in bulk.
 *
 * @returns {JSX.Element} The JSX structure for the
 *         ServicePartnerList component.
 */
export default function ConfigProductList() {
  // State and hooks initialization
  const navigate = useNavigate();
  const { post } = useFetch();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<TProduct[]>([]);
  const [filterParams, setFilterParams] =
    useState<TFilterModel>(defaultFilterParams);
  const { onShowToast } = useToast();
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});

  const initialColumns: any[] = [
    {
      title: "Product Name",
      dataIndex: "productName",
      key: "productName",
      width: "30%",
      filterSearch: true,
      filters: utils.getUniqueFilters(data, "productName"),
      sorter: (a: TProduct, b: TProduct) =>
        a.productName?.localeCompare(b?.productName),
      onFilter: (value: string | number | boolean, record: TProduct) =>
        record.productName?.includes(value as string),
    },
    {
      title: "Product Code",
      dataIndex: "productCode",
      key: "productCode",
      width: "15%",
      filterSearch: true,
      filters: utils.getUniqueFilters(data, "productCode"),
      sorter: (a: TProduct, b: TProduct) =>
        a.productCode?.localeCompare(b?.productCode),
      onFilter: (value: string | number | boolean, record: TProduct) =>
        record.productCode?.includes(value as string),
    },
    {
      title: "Gyro",
      dataIndex: "gyro",
      key: "gyro",
      width: "10%",
      filterSearch: true,
      filters: [
        { text: "Yes", value: 1 },
        { text: "No", value: 0 },
      ],
      sorter: (a: TProduct, b: TProduct) =>
        String(a.gyro).localeCompare(String(b.gyro)),
      onFilter: (value: string | number | boolean, record: TProduct) =>
        record.gyro === value,
      render: (gyro: number) => (gyro ? "Yes" : "No"),
    },
    {
      title: "Action",
      key: "actions",
      align: "center",
      width: "4%",
      render: (item: TProduct) => (
        <>
          <GenericPopover
            placement="left"
            content={
              <div className="standardActionBox">
                {/* Edit Row with CustomDrawer trigger */}
                {Utils.isUserHasAccess(
                  context.user?.privilegeList,
                  ePrivileges.EDITCONFIGPRODUCT
                ) ? (
                  <CustomDrawer
                    size="default"
                    trigger={
                      <ActionCard text="Edit">
                        <EditOutlined />
                      </ActionCard>
                    }
                    component={ConfigProductForm} // Passing ServicePartner component
                    componentProps={{
                      id: item.id,
                      productName: item.productName,
                      gyro: item.gyro,
                      productCode: item.productCode, // Pass necessary props for ServicePartner
                      onRefreshList: () => {
                        fetchData();
                      },
                    }}
                    title="Config Product Form"
                  />
                ) : null}

                {/* TODO ::: Add privilege check on delete action */}
                <ActionCard
                  text="Delete"
                  onClick={() => {
                    setIsModalVisible(true);
                    setSelectedProduct(item);
                  }}
                >
                  <DeleteOutlined />
                </ActionCard>
              </div>
            }
            icon={
              <MoreOutlined style={{ fontSize: "20px", cursor: "pointer" }} />
            }
          />
        </>
      ),
    },
  ];

  const [columns, setColumns] = useState(initialColumns);
  const [draftColumns, setDraftColumns] = useState(columns);
  const [searchText, setSearchText] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<TProduct | null>(null);
  const context: any = useContext(UserContext);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const requestPayload = {
        data: {
          currentPage: 1,
          pageSize: -1,
        },
      };

      const response = await post(GetProductList, requestPayload);
      const { data, dataResponse, filterModel } = response;
      const { returnCode, description } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        const formattedData = data.map((item: TProduct, index: number) => ({
          key: (item.id ?? "").toString(),
          srNo: index + 1,
          productName: item.productName,
          productCode: item.productCode,
          gyro: item.gyro,
          id: item.id,
        }));
        setData(formattedData);
        setFilterParams({ ...(filterModel as TFilterModel) });
        setIsLoading(false);
        updateColumnFilters(filteredData);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: description,
        });
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error:", error);
      setIsLoading(false);
    }
  };

  /**
   * Updates the dealer with the given values.
   * Sends a request to the API with the given values, and if the response is successful, shows a success toast and fetches the data again.
   * If the response is not successful, shows an error toast with the description from the response.
   * @param {TDealer} values - the dealer values to update.
   */

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: any
  ) => {
    setActiveFilters(filters);
    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if ("dataIndex" in col) {
          return {
            ...col,
            filteredValue: filters[col.dataIndex as string] || null,
            sortOrder: col.dataIndex === sorter.field ? sorter.order : null,
          };
        }
        return col;
      })
    );
  };

  const filteredData = data.filter((item) => {
    const searchMatch = Object.values(item).some((val) =>
      val?.toString().toLowerCase().includes(searchText.toLowerCase())
    );

    if (!searchMatch) return false;

    const productNameMatch =
      !activeFilters.productName ||
      !activeFilters.productName.length ||
      activeFilters.productName.includes(item.productName);

    const productCodeMatch =
      !activeFilters.productCode ||
      !activeFilters.productCode.length ||
      activeFilters.productCode.includes(item.productCode);

    const gyroMatch =
      !activeFilters.gyro ||
      !activeFilters.gyro.length ||
      activeFilters.gyro.includes(item.gyro);

    return searchMatch && productNameMatch && productCodeMatch && gyroMatch;
  });

  const updateColumnFilters = (dataset: TProduct[]) => {
    const nameFilters = Array.from(
      new Set(
        dataset
          .filter((item) => item.productName)
          .map((item) => item.productName)
      )
    ).map((productName) => ({ text: productName, value: productName }));

    const codeFilters = Array.from(
      new Set(
        dataset
          .filter((item) => item.productCode)
          .map((item) => item.productCode)
      )
    ).map((productCode) => ({
      text: productCode,
      value: productCode,
    }));

    const gyroFilters = [
      { text: "Yes", value: 1 },
      { text: "No", value: 0 },
    ].filter((option) => dataset.some((item) => item.gyro === option.value));

    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if (col.key === "productName") {
          return { ...col, filters: nameFilters };
        }
        if (col.key === "productCode") {
          return { ...col, filters: codeFilters };
        }
        if (col.key === "gyro") {
          return { ...col, filters: gyroFilters };
        }
        return col;
      })
    );
  };

  useEffect(() => {
    updateColumnFilters(filteredData);
  }, [activeFilters, data]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    setDraftColumns((prevDraftColumns) => {
      const reorderedColumns = Array.from(prevDraftColumns);
      const [removed] = reorderedColumns.splice(result.source.index, 1);
      reorderedColumns.splice(result.destination.index, 0, removed);
      return reorderedColumns;
    });
  };

  const toggleColumn = (key: string) => {
    setDraftColumns((prevDraftColumns) =>
      prevDraftColumns.map((col) =>
        col.key === key ? { ...col, hidden: !col.hidden } : col
      )
    );
  };

  const handleResetColumns = () => {
    setDraftColumns(initialColumns); // Reset staging state to initial state
  };

  useEffect(() => {
    const savedColumns = ColumnConfigManager.loadConfig(
      eColumnConfigKeys.ConfigProductList,
      initialColumns
    );
    setColumns(savedColumns);
    setDraftColumns(savedColumns);
  }, []);

  const handleSaveColumns = () => {
    ColumnConfigManager.saveConfig(
      eColumnConfigKeys.ConfigProductList,
      draftColumns
    );
    setColumns(draftColumns); // Apply changes to the final state
    setDrawerVisible(false); // Close the drawer
  };

  const visibleColumns = columns.filter((col) => !col.hidden);

  const exportHeaders = columns
    .filter(
      (column) =>
        column.key && (column?.title as string)?.toLowerCase() !== "action"
    )
    .map((column) => ({
      label: column.title,
      key: column.key,
    }));

  const exportData = data.map((user) => ({
    ...user,
  }));

  const handleResetAll = () => {
    setActiveFilters({});
    setSearchText("");
    setColumns((prevColumns) =>
      prevColumns.map((col) => ({
        ...col,
        filteredValue: null,
        sortOrder: null,
      }))
    );
  };

  const handleCancel = () => {
    setSelectedProduct(null);
    setIsModalVisible(false);
  };

  const handleOk = async () => {
    try {
      const payload = {
        data: {
          id: selectedProduct?.id,
          isDeleted: 1,
        },
      };
      setIsLoading(true);

      const response = await post(AddEditProduct, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
        fetchData();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
      setSelectedProduct(null);
      setIsModalVisible(false);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };
  // Return JSX structure for User Component
  return (
    <div className="pageBody">
      <PageHeader
        icon={<IdcardOutlined />}
        title={`${filterParams?.totalRows} Total Config Products`}
        size="large"
      >
        <Space>
          {Utils.isUserHasAccess(
            context.user?.privilegeList,
            ePrivileges.ADDCONFIGPRODUCT
          ) ? (
            <CustomDrawer
              size="default"
              trigger={
                <Button disabled={isLoading} type="primary">
                  Add
                </Button>
              }
              component={ConfigProductForm} // Passing Service Partner Form component
              componentProps={{
                id: 0, // Pass necessary props for ServicePartnerForm
                onRefreshList: () => {
                  fetchData();
                },
              }}
              title="Service Partner Form"
            />
          ) : null}
          <Input
            placeholder="Filter by keywords"
            value={searchText}
            onChange={handleSearch}
            style={{ width: 200, borderColor: "var(--bs-primary-light)" }}
            prefix={<SearchOutlined />}
          />
          <ExportToCSV
            data={exportData}
            headers={exportHeaders}
            fileName={`config_product_${DateUtils.format(new Date())}.csv`}
          />
          <Tooltip title="Reset Filters">
            <FilterOutlined onClick={handleResetAll} />
          </Tooltip>
          <Tooltip title="Configure Columns">
            <OrderedListOutlined onClick={() => setDrawerVisible(true)} />
          </Tooltip>
        </Space>
      </PageHeader>

      <div className="tableWrap">
        <Table
          columns={visibleColumns}
          dataSource={filteredData}
          loading={isLoading}
          pagination={{
            position: ["bottomLeft"],
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total: any, range: any[]) =>
              `Showing ${range[0]}-${range[1]} of ${total} total entries`,
          }}
          onChange={handleTableChange}
          bordered={false}
          size="small"
          sticky
          scroll={{
            y: "calc(100vh - 310px)", // Adjust height dynamically
          }}
        />
      </div>
      <Drawer
        title="Customize Columns"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        style={{ transition: "transform 0.3s ease-in-out" }}
      >
        <ColumnManager
          columns={draftColumns}
          initialColumns={initialColumns}
          onDragEnd={handleDragEnd}
          onToggleColumn={toggleColumn}
          onResetColumns={handleResetColumns}
          onSave={handleSaveColumns}
          onCancel={() => setDrawerVisible(false)}
        />
      </Drawer>

      {/* Modal to ask the confirmation before proceeding with delete operation */}
      <CustomModal
        title="Confirm"
        isOpen={isModalVisible} // Ensure selectedUserId is valid
        okText="Yes"
        cusHeader={true}
        cancelText="No"
        onOk={async () => await handleOk()}
        onCancel={() => handleCancel()}
      >
        <p>
          You are about to delete <b>{selectedProduct?.productName}</b>
        </p>
      </CustomModal>
    </div>
  );
}
